"use client";

import React from "react";
import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="size-12 rounded-full bg-chart-2/20 flex items-center justify-center mb-4">
          <div className="size-6 rounded-full bg-chart-2" />
        </div>
        <h3 className="text-xl font-bold text-primary mb-2">Bedankt!</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Nog een bericht sturen
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Naam is verplicht"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Naam</FormLabel>
            <FormControl>
              <Input placeholder="Uw volledige naam" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "E-mail is verplicht",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Voer een geldig e-mailadres in"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>E-mail</FormLabel>
            <FormControl>
              <Input type="email" placeholder="uw@email.nl" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Bericht is verplicht",
      minLength: {
        value: 10,
        message: "Bericht moet minimaal 10 tekens bevatten"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Bericht</FormLabel>
            <FormControl>
              <Textarea placeholder="Hoe kan EduMedius u ondersteunen?" className="min-h-[120px] resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6 text-lg font-bold">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Verzenden...
          </> : "Verstuur aanvraag"}
      </Button>
    </Form>;
}
export default function UwWoningAlsRendement() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="78838" id="midden-in-het-onderwijs" className="bg-background text-foreground relative min-h-[90vh] flex items-center justify-center overflow-hidden py-24">
      {}
      <div className="absolute inset-y-0 left-0 w-full md:w-1/2 bg-[url(/img/edumedius_flow_left.svg)] bg-no-repeat bg-left bg-contain opacity-10 pointer-events-none animate-pulse" style={{
      animationDuration: "10s"
    }} />
      <div className="absolute inset-y-0 right-0 w-full md:w-1/2 bg-[url(/img/edumedius_flow_right.svg)] bg-no-repeat bg-right bg-contain opacity-10 pointer-events-none animate-pulse" style={{
      animationDuration: "12s",
      animationDelay: "2s"
    }} />

      {}
      <div ref={ref} className={`relative z-10 container mx-auto px-4 flex flex-col items-center text-center transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <h1 className="font-default text-5xl md:text-7xl lg:text-8xl leading-[1.1] font-extrabold tracking-tight text-primary uppercase mb-8 max-w-6xl drop-shadow-md">
          EduMedius - midden in het Onderwijs
        </h1>

        <p className="font-default text-lg md:text-2xl text-muted-foreground font-medium tracking-normal max-w-3xl mb-14 leading-relaxed">
          De strategische partner voor HBO-onderwijsinstellingen. Wij garanderen kwaliteit en continuïteit door de inzet van ervaren, BKE-gecertificeerde assessoren en onderwijsprofessionals.
        </p>

        <Dialog>
          <DialogTrigger asChild>
            <Button size="lg" className="rounded-lg bg-gradient-to-r from-primary to-chart-2 text-primary-foreground hover:shadow-[0_0_40px_rgba(32,164,173,0.5)] transition-all duration-500 px-8 py-8 md:px-12 md:py-10 text-base md:text-xl tracking-wider uppercase font-bold group border-none cursor-pointer">
              Maak kennis met EduMedius
              <ArrowRight className="ml-4 size-6 transition-transform duration-500 group-hover:translate-x-3" />
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden border-none bg-background shadow-2xl">
            <div className="p-6 md:p-8">
              <DialogHeader className="mb-6">
                <DialogTitle className="text-3xl font-extrabold text-primary tracking-tight">
                  Kennismaken
                </DialogTitle>
                <p className="text-muted-foreground mt-2">
                  Laat uw gegevens achter en wij nemen zo snel mogelijk contact met u op.
                </p>
              </DialogHeader>

              <FormProvider formId="kennismaking-contact-hero" sectionName="UwWoningAlsRendement" defaultValues={{
              name: "",
              email: "",
              message: ""
            }} submitText="Verstuur aanvraag" successMessage="Bedankt voor uw interesse. We nemen spoedig contact met u op.">
                <ContactForm />
              </FormProvider>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>;
}